/**
 * VTT — canvas map, grid, pan/zoom, drawing tools, live sync.
 *
 * Stage E: the SceneClient bridges the local store with the API Gateway
 * scene protocol. Local upserts go out as `scene/op` messages; incoming
 * ops flow through the store's reconciler.
 *
 * Map + session selection is still a placeholder ("local-dev") — a real
 * campaign/session picker will land on the Dashboard later.
 */
import { useEffect, useState } from "react";
import SceneCanvas from "../vtt/canvas/SceneCanvas";
import ToolPalette from "../vtt/ui/ToolPalette";
import AssetLibrary from "../vtt/ui/AssetLibrary";
import SizePicker from "../vtt/ui/SizePicker";
import LayerPicker from "../vtt/ui/LayerPicker";
import Toasts, { toastStore } from "../vtt/ui/Toasts";
import { sceneStore } from "../vtt/store/sceneStore";
import { SceneClient } from "../vtt/net/SceneClient";
import { currentSceneClient } from "../vtt/net/currentClient";
import { useAuth } from "../lib/AuthContext";
import { isAdmin } from "../lib/auth";
import "./Vtt.css";

type Status = "idle" | "connecting" | "open" | "reconnecting" | "closed" | "disabled";

const DEFAULT_MAP_ID = "local-dev";
const DEFAULT_SESSION_ID = "local-dev";
// Asset library is scoped per campaign; reuse the same placeholder the
// scene sync uses until the dashboard wires a real campaign picker.
const DEFAULT_CAMPAIGN_ID = "local-dev";

export default function Vtt() {
  const { user } = useAuth();
  const [status, setStatus] = useState<Status>("idle");
  const [lastError, setLastError] = useState<string | null>(null);

  useEffect(() => {
    // Seed store identity from the Cognito session.
    sceneStore.setMap(DEFAULT_MAP_ID);
    if (user) {
      const uid = user.username || user.email || "anonymous";
      sceneStore.setIdentity(uid, isAdmin(user));
    }

    // If there's no websocket URL configured (local dev without the CF
    // stack deployed), skip the socket entirely — the canvas still works
    // against the local store and undo.
    if (!import.meta.env.VITE_WS_URL) {
      setStatus("disabled");
      return;
    }
    if (!user) return; // wait for auth

    const client = new SceneClient(
      sceneStore,
      { sessionId: DEFAULT_SESSION_ID, mapId: DEFAULT_MAP_ID },
      {
        status: (s) => setStatus(s),
        error: (code, message) => {
          setLastError(message);
          // Layer permission rejections deserve a visible toast — they're
          // the only errors the user can do anything about. Version-
          // conflict rebases and transport hiccups stay silent.
          if (code === "op-rejected" || code === "layer-forbidden") {
            toastStore.push("warn", `Can't edit that element: ${message}`);
          }
        },
      },
    );
    currentSceneClient.instance = client;
    client.start().catch((err) => {
      console.warn("[Vtt] SceneClient failed to start", err);
      setStatus("closed");
    });

    return () => {
      if (currentSceneClient.instance === client) {
        currentSceneClient.instance = null;
      }
      client.stop();
    };
  }, [user]);

  return (
    <div className="vtt-page">
      <SceneCanvas />
      <ToolPalette />
      <AssetLibrary campaignId={DEFAULT_CAMPAIGN_ID} />
      <SizePicker />
      <LayerPicker />
      <Toasts />
      <ConnectionBadge status={status} error={lastError} />
    </div>
  );
}

function ConnectionBadge({ status, error }: { status: Status; error: string | null }) {
  // Tiny top-right indicator so the DM can see when live sync drops.
  const label: Record<Status, string> = {
    idle: "idle",
    connecting: "connecting…",
    open: "live",
    reconnecting: "reconnecting…",
    closed: "offline",
    disabled: "offline (dev)",
  };
  const tone: Record<Status, string> = {
    idle: "#a8a4b3",
    connecting: "#f5c77a",
    open: "#6ac1c4",
    reconnecting: "#f5c77a",
    closed: "#f87171",
    disabled: "#a8a4b3",
  };
  return (
    <div className="vtt-badge" style={{ color: tone[status] }} title={error ?? label[status]}>
      <span className="vtt-badge__dot" style={{ background: tone[status] }} />
      {label[status]}
    </div>
  );
}
